/**
 * @sunflowerlab
 * @author Ashish Kumar
 */

import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { AppConstants } from './app.constant';

export class ErrorMessages {
  public static readonly errorMessage: string = 'Something went wrong, please try again later.';
  public static readonly serverNotAvailable: string = 'Server is not available at the moment. Please try after sometime.';
  public static readonly INVALID_LOGIN: string = 'The login detail is incorrect.';
  public static readonly csvParserError = 'Errors found in rows. Please correct data and try re-importing.';
  public static readonly notPermitted: string = 'You do not have permission to perform this action.';
  public static readonly importRowsRefused: string = 'Nothing was saved. Fix these rows and import again:';
  public static readonly importRateLimited: string = 'Too many imports at once. Please wait a moment and try again.';
  public static readonly importBusy: string = 'Another save is in progress for some of these days, so nothing was saved. Please upload again.';
}

export class PermissionDenial {
  public static readonly SERVER_MESSAGE = 'Sorry, your role does not permit this action';

  public static isTenantRoleDenial(errorCode: number, message: string, method: string, url: string): boolean {
    if (errorCode !== 403) {
      return false;
    }
    if ((message || '').toLowerCase().includes('does not permit this action')) {
      return true;
    }
    return !PermissionDenial.isPositionWrite(method, url) && !PermissionDenial.isWorkExceptionWrite(method, url);
  }

  // A 403 on these routes explains itself (e.g. an approved day only a work_exceptions: manage user may change), so show the server's text.
  public static isWorkExceptionWrite(method: string, url: string): boolean {
    const verb = (method || '').toUpperCase();
    const path = (url || '').split('?')[0].replace(/\/+$/, '');
    if (verb === 'POST') {
      return /\/work_exceptions\/(approve|reject)$/.test(path);
    }
    if (verb === 'PUT' || verb === 'DELETE') {
      return /\/work_exceptions\/\d+$/.test(path);
    }
    return false;
  }

  public static isPositionWrite(method: string, url: string): boolean {
    const verb = (method || '').toUpperCase();
    if (verb !== 'POST' && verb !== 'PUT') {
      return false;
    }
    const path = (url || '').split('?')[0].replace(/\/+$/, '');
    return /\/positions(\/\d+)?$/.test(path);
  }
}

/**
 * The API reports business-rule refusals as a guard key joined to its own text,
 * e.g. 'effective_end_date_exists: Unable to update position'. Those keys are
 * internal names and read as noise in a toast, so translate the ones we recognise.
 * Anything unmapped falls through to the server's text unchanged, so a new guard is
 * never swallowed - it just shows up raw until someone maps it here.
 */
export class ServerErrorMessages {
  private static readonly SEPARATOR = ': ';

  private static readonly BY_KEY: Record<string, string> = {
    effective_end_date_exists: 'This record already has an effective end date recorded against it and can no longer be edited. Please contact your administrator.'
  };

  // Keys whose own text already reads fine on its own, so only the key prefix is dropped.
  private static readonly STRIP_KEYS = ['status'];

  // Refusals whose key is shared with other errors, so they are matched on their whole text.
  private static readonly BY_PATTERN: { pattern: RegExp; message: string }[] = [
    {
      // Approve/reject of a time-off day someone else already reviewed or changed; the screen reloads the list after it.
      pattern: /^ids: work exception (\d+ )?is not current/,
      message: 'One or more of the selected days was already approved, rejected or changed by someone else. The list has been refreshed, please review and try again.'
    },
    {
    
      pattern: /^customer_id: must be an active client/,
      message: 'This client is inactive. Activate the client before creating or copying its projects.'
    }
  ];


  private static readonly FIELD_REFUSAL = /^([a-z_]+): ([\s\S]+)$/;

  public static translate(raw: string): string {
    const match = ServerErrorMessages.BY_PATTERN.find((entry) => entry.pattern.test(raw));
    if (match) {
      return match.message;
    }
    const at = raw.indexOf(ServerErrorMessages.SEPARATOR);
    if (at === -1) {
      return raw;
    }
    const key = raw.slice(0, at);
    if (ServerErrorMessages.STRIP_KEYS.includes(key)) {
      const text = raw.slice(at + ServerErrorMessages.SEPARATOR.length).trim();
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
    return ServerErrorMessages.BY_KEY[key] || raw;
  }

  public static refusal(error: HttpErrorResponse): string | null {
    const errors = error?.error?.errors;
    return error?.status === AppConstants.unprocessableEntityStatus && errors ? ServerErrorMessages.translate(errors.toString()) : null;
  }

  public static fieldRefusals(error: HttpErrorResponse): { field: string | null; message: string }[] {
    const errors = error?.status === AppConstants.unprocessableEntityStatus ? error?.error?.errors : null;
    if (!errors) {
      return [];
    }
    return [].concat(errors).map((raw) => {
      const match = ServerErrorMessages.FIELD_REFUSAL.exec(String(raw).trim());
      return match ? { field: match[1], message: match[2] } : { field: null, message: String(raw) };
    });
  }

  public static applyToForm(form: FormGroup, error: HttpErrorResponse): string[] {
    const unplaced: string[] = [];
    ServerErrorMessages.fieldRefusals(error).forEach(({ field, message }) => {
      const control = field ? form.get(field) : null;
      if (control) {
        control.setErrors({ ...control.errors, server: message });
        control.markAsTouched();
      } else {
        unplaced.push(field ? `${field}: ${message}` : message);
      }
    });
    return unplaced;
  }
}
